import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faUserCheck,
    faClock,
    faSearch,
    faFilter,
    faEye,
    faCheck,
    faChevronLeft,
    faChevronRight,
    faSpinner,
    faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import SuccessModal from '../../components/SuccessModal';
import ConfirmationModal from '../../components/ConfirmationModal';

const PaymentVerification = () => {
    const navigate = useNavigate();
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filters
    const [statusFilter, setStatusFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [perPage, setPerPage] = useState(10);

    // Modals
    const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [processing, setProcessing] = useState(false);

    // Delegation state
    const [delegationUsers, setDelegationUsers] = useState([]);
    const [showDelegationModal, setShowDelegationModal] = useState(false);
    const showDelegationModalDismiss = useModalDismiss(() => setShowDelegationModal(false), { backdropClose: false, enabled: showDelegationModal });
    const [delegate1Id, setDelegate1Id] = useState('');
    const [delegate2Id, setDelegate2Id] = useState('');
    const [processingDelegation, setProcessingDelegation] = useState(false);

    // Success modal
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        fetchBatches();
        fetchDelegationUsers();
    }, [currentPage, statusFilter, searchTerm, dateFrom, dateTo]);

    const fetchBatches = async () => {
        try {
            setLoading(true);
            const response = await api.get('/payments/pending-verification', {
                params: {
                    page: currentPage,
                    per_page: perPage,
                    status: statusFilter,
                    search: searchTerm,
                    date_from: dateFrom,
                    date_to: dateTo,
                },
            });

            if (response.data.success) {
                setBatches(response.data.data.data);
                setCurrentPage(response.data.data.pagination.current_page);
                setTotalPages(response.data.data.pagination.last_page);
                setTotal(response.data.data.pagination.total);
            }
        } catch (err) {
            console.error('Error fetching batches:', err);
            setError('Erreur lors du chargement des lots en attente de vérification');
        } finally {
            setLoading(false);
        }
    };


    const handleVerify = async () => {
        try {
            setProcessing(true);
            const response = await api.post(`/payments/${selectedBatch.id}/verify`);

            if (response.data.success) {
                setSuccessMessage('Lot vérifié avec succès');
                setShowSuccessModal(true);
                setShowVerifyModal(false);
                fetchBatches();
            }
        } catch (err) {
            console.error('Error verifying batch:', err);
            if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError('Erreur lors de la vérification du lot');
            }
        } finally {
            setProcessing(false);
        }
    };

    const openVerifyModal = (batch) => {
        setSelectedBatch(batch);
        setShowVerifyModal(true);
    };

    // Delegation functions
    const fetchDelegationUsers = async () => {
        try {
            const response = await api.get('/payments/delegation-users');
            if (response.data.success) {
                setDelegationUsers(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching delegation users:', err);
        }
    };

    const openDelegationModal = (batch) => {
        setSelectedBatch(batch);
        setShowDelegationModal(true);
    };

    const handleDelegation = async () => {
        if (!delegate1Id || !delegate2Id) {
            setError('Veuillez sélectionner les deux délégués');
            return;
        }

        try {
            setProcessingDelegation(true);
            const response = await api.post(`/payments/${selectedBatch.id}/set-delegation`, {
                delegate_1_id: parseInt(delegate1Id),
                delegate_2_id: parseInt(delegate2Id),
            });

            if (response.data.success) {
                setSuccessMessage('Délégation définie avec succès');
                setShowSuccessModal(true);
                setShowDelegationModal(false);
                setDelegate1Id('');
                setDelegate2Id('');
                fetchBatches();
            }
        } catch (err) {
            console.error('Error setting delegation:', err);
            setError(err.response?.data?.message || 'Erreur lors de la définition de la délégation');
        } finally {
            setProcessingDelegation(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'draft': return <FontAwesomeIcon icon={faClock} className="text-gray-500" />;
            case 'locked': return <FontAwesomeIcon icon={faUserCheck} className="text-yellow-500" />;
            case 'validated': return <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />;
            case 'exported': return <FontAwesomeIcon icon={faCheck} className="text-blue-500" />;
            default: return <FontAwesomeIcon icon={faClock} className="text-gray-500" />;
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'draft': return 'Brouillon';
            case 'locked': return 'En attente de vérification';
            case 'validated': return 'Validé';
            case 'exported': return 'Exporté';
            default: return status;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'draft': return 'text-gray-600';
            case 'locked': return 'text-yellow-600';
            case 'validated': return 'text-green-600';
            case 'exported': return 'text-blue-600';
            default: return 'text-gray-600';
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'MAD',
            minimumFractionDigits: 2,
        }).format(amount);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('fr-FR');
    };

    const translateVerificationStatus = (status) => {
        if (!status) return '';
        
        // Translate role names
        const roleTranslations = {
            'administrateur_general_superieur': 'Business Officer',
            'administrateur_general': 'Administrateur Général',
            'chef_terrain': 'Chef de Terrain',
            'chef_terrain_superieur': 'Head of operations',
            'resp_cati': 'Responsable CATI',
            'resp_controle': 'Responsable Contrôle',
            'resp_admin': 'Responsable Admin',
            'charge_etude': 'Chargé d\'Étude',
            'charge_etude_superieur': 'Chargé d\'Étude Supérieur'
        };

        // Handle specific patterns
        if (status.includes('Not in verification status')) {
            return 'Pas en statut de vérification';
        }

        // Replace role names first (before other translations)
        let translated = status;
        Object.entries(roleTranslations).forEach(([key, value]) => {
            const regex = new RegExp(key, 'gi');
            translated = translated.replace(regex, value);
        });

        // Translate common phrases
        translated = translated.replace(/Pending/gi, 'En attente de');
        translated = translated.replace(/verification/gi, 'vérification');
        translated = translated.replace(/by/gi, 'par');
        translated = translated.replace(/timeout/gi, 'délai');
        translated = translated.replace(/testing/gi, 'test');
        translated = translated.replace(/final/gi, 'final');
        translated = translated.replace(/\(/g, '(');
        translated = translated.replace(/\)/g, ')');
        translated = translated.replace(/4h timeout/gi, 'délai de 4h');
        translated = translated.replace(/8h timeout/gi, 'délai de 8h');
        translated = translated.replace(/2 min timeout/gi, 'délai de 2 min'); // Keep for backward compatibility
        translated = translated.replace(/4 min timeout/gi, 'délai de 4 min'); // Keep for backward compatibility

        return translated;
    };

    const hasActiveFilters = statusFilter || searchTerm || dateFrom || dateTo;

    const clearFilters = () => {
        setStatusFilter('');
        setSearchTerm('');
        setDateFrom('');
        setDateTo('');
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    if (loading && batches.length === 0) {
        return (
            <div className="flex flex-col sm:flex-row items-center justify-center h-64 p-4">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xl sm:text-2xl text-red-600 mb-2 sm:mb-0 sm:mr-2" />
                <span className="text-sm sm:text-lg text-center">Chargement des lots en attente de vérification...</span>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
                    <div className="mb-4">
                        <div>
                            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                                Vérification des Lots de Paiement
                            </h1>
                            <p className="text-sm sm:text-base text-gray-600 mt-1">
                                Vérifiez et validez les lots de paiement.
                            </p>
                        </div>
                    </div>

                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mb-4">
                        <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                            Filtres
                        </h2>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center justify-center px-3 sm:px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                                showFilters 
                                    ? 'text-white bg-red-600 hover:bg-red-700' 
                                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            <FontAwesomeIcon icon={faFilter} className="mr-1 sm:mr-2" />
                            Filtres
                        </button>
                    </div>

                    {showFilters && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                    Date de début
                                </label>
                                <input
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                />
                            </div>

                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                    Date de fin
                                </label>
                                <input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                />
                            </div>

                            <div>
                                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                    Rechercher
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                        placeholder="ID du lot, enquête..."
                                        className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 min-w-0"
                                    />
                                    <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-3 text-gray-400" />
                                </div>
                            </div>

                            <div className="flex items-end">
                                {hasActiveFilters && (
                                    <button
                                        onClick={clearFilters}
                                        className="w-full sm:w-auto px-4 py-2 text-xs sm:text-sm font-medium text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                                    >
                                        Effacer les filtres
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <p className="text-red-800">{error}</p>
                        <button
                            onClick={() => setError('')}
                            className="mt-2 text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                            Fermer
                        </button>
                    </div>
                )}

                {/* Batches Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    {batches.length === 0 ? (
                        <div className="text-center py-8 sm:py-12 px-4">
                            <FontAwesomeIcon icon={faUserCheck} className="text-3xl sm:text-4xl text-gray-400 mb-4" />
                            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                                Aucun lot en attente de vérification
                            </h3>
                            <p className="text-sm sm:text-base text-gray-600">
                                Tous les lots ont été vérifiés ou aucun lot n'est en attente.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop Table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Lot
                                            </th>
                                            <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Période
                                            </th>
                                            <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Montant
                                            </th>
                                            <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Statut de Vérification
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {batches.map((batch) => (
                                            <tr key={batch.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/payments/${batch.id}`)}>
                                                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {batch.creation_mode === 'express' ? 'Lot de frais de déplacement' : 'Lot de paiement'} #{batch.id}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {batch.lines_count} lignes
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <div>
                                                        {formatDate(batch.period_start)} - {formatDate(batch.period_end)}
                                                    </div>
                                                </td>
                                                <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {batch.creation_mode === 'express' && parseFloat(batch.credit_applied || 0) > 0 ? (
                                                        <>
                                                            <div className="font-medium text-purple-700">{formatCurrency(batch.net_a_decaisser)}</div>
                                                            <div className="text-xs text-gray-400 line-through">{formatCurrency(batch.totals_net)}</div>
                                                            <div className="text-[10px] text-gray-400">−{formatCurrency(batch.credit_applied)} caisse</div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="font-medium">{formatCurrency(batch.totals_net)}</div>
                                                            <div className="text-xs text-gray-500">
                                                                Brut: {formatCurrency(batch.totals_gross)}
                                                            </div>
                                                        </>
                                                    )}
                                                </td>
                                                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        {getStatusIcon(batch.status)}
                                                        <span className={`ml-2 text-sm font-medium ${getStatusColor(batch.status)}`}>
                                                            {batch.status === 'draft'
                                                                ? 'Pas encore prêt pour vérification'
                                                                : translateVerificationStatus(batch.verification_status)}
                                                        </span>
                                                    </div>
                                                    {batch.delegate_verification_to_1 && (
                                                        <div className="text-xs text-gray-500 mt-1">
                                                            Délégation active
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Cards */}
                            <div className="block md:hidden">
                                <div className="divide-y divide-gray-200">
                                    {batches.map((batch) => (
                                        <div key={batch.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/payments/${batch.id}`)}>
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-base font-medium text-gray-900 mb-1">
                                                        {batch.creation_mode === 'express' ? 'Lot de frais de déplacement' : 'Lot de paiement'} #{batch.id}
                                                    </div>
                                                    <div className="text-sm text-gray-500 mb-2">
                                                        {batch.lines_count} lignes
                                                    </div>
                                                    <div className="text-xs text-gray-600 mb-2 break-words">
                                                        {formatDate(batch.period_start)} - {formatDate(batch.period_end)}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="mb-3">
                                                {batch.creation_mode === 'express' && parseFloat(batch.credit_applied || 0) > 0 ? (
                                                    <>
                                                        <div className="text-base font-medium text-purple-700">
                                                            {formatCurrency(batch.net_a_decaisser)}
                                                        </div>
                                                        <div className="text-xs text-gray-400 line-through">{formatCurrency(batch.totals_net)}</div>
                                                        <div className="text-[10px] text-gray-400">−{formatCurrency(batch.credit_applied)} caisse</div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div className="text-base font-medium text-gray-900">
                                                            {formatCurrency(batch.totals_net)}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            Brut: {formatCurrency(batch.totals_gross)}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            <div className="mb-3">
                                                <div className="flex items-center">
                                                    {getStatusIcon(batch.status)}
                                                    <span className={`ml-2 text-sm font-medium ${getStatusColor(batch.status)}`}>
                                                        {batch.status === 'draft'
                                                            ? 'Pas encore prêt pour vérification'
                                                            : translateVerificationStatus(batch.verification_status)}
                                                    </span>
                                                </div>
                                                {batch.delegate_verification_to_1 && (
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        Délégation active
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="bg-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200 sm:px-6">
                                    <div className="flex-1 flex justify-between sm:hidden w-full">
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="relative inline-flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Précédent
                                        </button>
                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="relative inline-flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Suivant
                                        </button>
                                    </div>
                                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between w-full">
                                        <div>
                                            <p className="text-xs sm:text-sm text-gray-700 whitespace-nowrap">
                                                Affichage de{' '}
                                                <span className="font-medium">{(currentPage - 1) * perPage + 1}</span>
                                                {' '}à{' '}
                                                <span className="font-medium">
                                                    {Math.min(currentPage * perPage, total)}
                                                </span>
                                                {' '}sur{' '}
                                                <span className="font-medium">{total}</span>
                                                {' '}résultats
                                            </p>
                                        </div>
                                        <div>
                                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                                <button
                                                    onClick={() => handlePageChange(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                                                >
                                                    <FontAwesomeIcon icon={faChevronLeft} />
                                                </button>
                                                {[...Array(totalPages)].map((_, index) => {
                                                    const page = index + 1;
                                                    const isCurrentPage = page === currentPage;
                                                    const isNearCurrentPage = Math.abs(page - currentPage) <= 2;
                                                    
                                                    if (!isNearCurrentPage && page !== 1 && page !== totalPages) {
                                                        return null;
                                                    }
                                                    
                                                    return (
                                                        <button
                                                            key={page}
                                                            onClick={() => handlePageChange(page)}
                                                            className={`relative inline-flex items-center px-3 sm:px-4 py-2 border text-xs sm:text-sm font-medium flex-shrink-0 ${
                                                                isCurrentPage
                                                                    ? 'z-10 bg-red-50 border-red-500 text-red-600'
                                                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                            }`}
                                                        >
                                                            {page}
                                                        </button>
                                                    );
                                                })}
                                                <button
                                                    onClick={() => handlePageChange(currentPage + 1)}
                                                    disabled={currentPage === totalPages}
                                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                                                >
                                                    <FontAwesomeIcon icon={faChevronRight} />
                                                </button>
                                            </nav>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>


            {/* Verify Confirmation Modal */}
            <ConfirmationModal
                isOpen={showVerifyModal}
                onClose={() => setShowVerifyModal(false)}
                onConfirm={handleVerify}
                title="Confirmer la vérification"
                message={`Êtes-vous sûr de vouloir vérifier et valider le lot #${selectedBatch?.id} ?`}
                confirmText="Vérifier"
                isLoading={processing}
                confirmColor="bg-red-600 hover:bg-red-700"
            />

            {/* Delegation Modal */}
            {showDelegationModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
                    <div className="relative top-10 sm:top-20 mx-auto p-4 sm:p-5 border w-full max-w-md sm:w-96 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
                        <div className="mt-3">
                            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">
                                Déléguer la vérification (Lot #{selectedBatch?.id})
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-600 mb-4">
                                Définissez les deux utilisateurs qui pourront vérifier ce lot spécifique après 4 heures si vous ne le faites pas.
                            </p>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                        Premier délégué (après 4 heures)
                                    </label>
                                    <select
                                        value={delegate1Id}
                                        onChange={(e) => setDelegate1Id(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                    >
                                        <option value="">Sélectionner un utilisateur</option>
                                        {delegationUsers.map(user => (
                                            <option key={user.id} value={user.id}>
                                                {user.name} ({user.role})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                                        Deuxième délégué (après 8 heures)
                                    </label>
                                    <select
                                        value={delegate2Id}
                                        onChange={(e) => setDelegate2Id(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                    >
                                        <option value="">Sélectionner un utilisateur</option>
                                        {delegationUsers.map(user => (
                                            <option key={user.id} value={user.id}>
                                                {user.name} ({user.role})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row justify-end gap-3 sm:space-x-3 mt-6">
                                <button
                                    onClick={() => setShowDelegationModal(false)}
                                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                                    disabled={processingDelegation}
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={handleDelegation}
                                    disabled={processingDelegation || !delegate1Id || !delegate2Id}
                                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                >
                                    {processingDelegation && (
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                    )}
                                    Définir la délégation
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                message={successMessage}
            />
        </div>
    );
};

export default PaymentVerification;
